import { useEffect, useMemo, useState } from 'react';
import { adminApi } from '@/lib/api';
import { Users, Calendar, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationPrevious,
  PaginationNext,
} from '@/components/ui/pagination';
import { toast } from 'sonner';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(amount || 0);
};

export default function ReportsStaffPage() {
  const [period, setPeriod] = useState('month');
  const [loading, setLoading] = useState(true);
  const [staffData, setStaffData] = useState(null);
  const [chartKey, setChartKey] = useState(0);
  const [tablePage, setTablePage] = useState(1);
  const [tablePageSize] = useState(10);
  const [tableTotal, setTableTotal] = useState(0);

  useEffect(() => {
    setTablePage(1);
  }, [period]);

  useEffect(() => {
    fetchStaffReport();
  }, [period, tablePage]);

  useEffect(() => {
    setChartKey((prev) => prev + 1);
  }, [staffData]);

  const fetchStaffReport = async () => {
    setLoading(true);
    try {
      const response = await adminApi.getStaffPerformance(period, {
        table_page: tablePage,
        table_page_size: tablePageSize,
      });
      setStaffData(response.data);
      setTableTotal(response.data.staff_performance_total || 0);
    } catch (error) {
      toast.error('Failed to fetch staff report');
    } finally {
      setLoading(false);
    }
  };

  const staffChartData = useMemo(
    () => (staffData?.staff_performance_chart || staffData?.staff_performance || []).map((staff) => ({
      ...staff,
      period_order_amount: Number(staff.period_order_amount || 0),
      period_payments_collected: Number(staff.period_payments_collected || 0),
    })),
    [staffData]
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#C97A5E]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="reports-staff-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#2D2420]">Reports · Staff Performance</h1>
          <p className="text-[#A8A8A8] mt-1">Team revenue and collections</p>
        </div>
        <div className="flex items-center gap-2">
          <Calendar className="w-5 h-5 text-[#A8A8A8]" />
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-40 border-[#E5E5E5]" data-testid="period-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="week">Last 7 Days</SelectItem>
              <SelectItem value="month">This Month</SelectItem>
              <SelectItem value="year">This Year</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-white border-[#E5E5E5]">
          <CardContent className="p-4">
            <p className="text-sm text-[#A8A8A8]">Total Staff</p>
            <p className="text-2xl font-bold text-[#2D2420]">{staffData?.total_staff || 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-white border-[#E5E5E5]">
          <CardContent className="p-4">
            <p className="text-sm text-[#A8A8A8]">Active Staff</p>
            <p className="text-2xl font-bold text-[#4A7c59]">{staffData?.active_staff || 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-white border-[#E5E5E5]">
          <CardContent className="p-4">
            <p className="text-sm text-[#A8A8A8]">Top Performer Revenue</p>
            <p className="text-2xl font-bold text-[#C97A5E]">
              {formatCurrency(staffData?.staff_performance?.[0]?.period_order_amount)}
            </p>
          </CardContent>
        </Card>
      </div>

      <Card className="bg-white border-[#E5E5E5]">
        <CardHeader>
          <CardTitle className="text-lg text-[#2D2420]">Staff Performance Comparison</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart key={`staff-${chartKey}`} data={staffChartData.slice(0, 10)} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E5" />
                <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} />
                <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} width={100} />
                <Tooltip formatter={(value) => formatCurrency(value)} />
                <Bar dataKey="period_order_amount" fill="#C97A5E" name="Orders" radius={[0, 4, 4, 0]} />
                <Bar dataKey="period_payments_collected" fill="#4A7c59" name="Collections" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-white border-[#E5E5E5]">
        <CardHeader>
          <CardTitle className="text-lg text-[#2D2420]">Detailed Performance</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#FAFAF9] border-b border-[#E5E5E5]">
                <tr>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Name</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Orders</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Revenue</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Collected</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Outstanding</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Customers</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E5E5E5]">
                {staffData?.staff_performance?.map((staff) => (
                  <tr key={staff.staff_id} className="hover:bg-[#FAFAF9]/50">
                    <td className="py-3 px-4 font-medium text-[#2D2420]">{staff.name}</td>
                    <td className="py-3 px-4 text-[#2D2420]">{staff.period_orders}</td>
                    <td className="py-3 px-4 text-[#2D2420]">{formatCurrency(staff.period_order_amount)}</td>
                    <td className="py-3 px-4 text-[#4A7c59]">{formatCurrency(staff.period_payments_collected)}</td>
                    <td className="py-3 px-4">
                      <span className={staff.total_outstanding > 0 ? 'text-[#E76F51]' : 'text-[#4A7c59]'}>
                        {formatCurrency(staff.total_outstanding)}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-[#2D2420]">{staff.customer_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
        {tableTotal > 0 && (
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 px-4 py-4 border-t border-[#E5E5E5]">
            <p className="text-sm text-[#A8A8A8]">
              Showing {(tablePage - 1) * tablePageSize + 1} to {Math.min(tablePage * tablePageSize, tableTotal)} of {tableTotal}
            </p>
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    onClick={(e) => {
                      e.preventDefault();
                      setTablePage((prev) => Math.max(1, prev - 1));
                    }}
                    className={tablePage === 1 ? 'pointer-events-none opacity-50' : ''}
                  />
                </PaginationItem>
                <PaginationItem>
                  <span className="px-3 text-sm text-[#5C4033]">
                    Page {tablePage} of {Math.max(1, Math.ceil(tableTotal / tablePageSize))}
                  </span>
                </PaginationItem>
                <PaginationItem>
                  <PaginationNext
                    onClick={(e) => {
                      e.preventDefault();
                      setTablePage((prev) => Math.min(Math.max(1, Math.ceil(tableTotal / tablePageSize)), prev + 1));
                    }}
                    className={tablePage >= Math.max(1, Math.ceil(tableTotal / tablePageSize)) ? 'pointer-events-none opacity-50' : ''}
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>
        )}
      </Card>
    </div>
  );
}
